import {
    AnswerEntityId,
    IAnswer,
    LayoutSectionKey,
    LayoutSectionParentKey,
    QuestionSource,
    ReferenceBusinessId,
    ReferralTypesOptions,
} from "@sp-crm/core";
import { LayoutFormSection } from "components/layout/layout-form-section";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { AutosavingInput } from "components/ui/autosaving-input";
import { PrimaryButton } from "components/ui/primary-button";
import { SelectPro } from "components/ui/select-pro";
import { Spinner } from "components/ui/spinner";
import {
    GetReferenceBusinessQuery,
    MutationPatchReferenceBusinessArgs,
    useGetLayoutContainerByKeyQuery,
    useGetReferenceBusinessQuery,
    usePatchReferenceBusinessMutation,
} from "generated/graphql";
import { produce } from "immer";
import React, { useCallback } from "react";
import { useQueryClient } from "react-query";
import { stableQueryOptions } from "util/requests";

interface NewInlineOrganizationProps {
    referenceBusinessId: ReferenceBusinessId | null;
    dismiss: () => void;
}

export const NewInlineOrganization: React.FC<NewInlineOrganizationProps> = props => {
    const { referenceBusinessId, dismiss } = props;

    if (!referenceBusinessId) {
        return <Spinner />;
    }
    return (
        <div className="space-y-2 lg:space-y-4 pr-2 lg:pr-4">
            <InlineOrganizationEdit referenceId={referenceBusinessId} />
            <div className="flex justify-end">
                <PrimaryButton onClick={dismiss}>Done</PrimaryButton>
            </div>
        </div>
    );
};

interface InlineOrganizationEditProps {
    referenceId: ReferenceBusinessId;
}

const InlineOrganizationEdit: React.FC<InlineOrganizationEditProps> = props => {
    const { referenceId } = props;
    const getReferenceBusinessQuery = useGetReferenceBusinessQuery(
        { id: referenceId },
        { keepPreviousData: true },
    );
    const patchReferenceBusinessMutation = usePatchReferenceBusinessMutation();
    const client = useQueryClient();
    const onChange = useCallback(
        async (args: MutationPatchReferenceBusinessArgs) => {
            const result = await patchReferenceBusinessMutation.mutateAsync(args);

            if (result?.patchReferenceBusiness) {
                client.setQueryData<GetReferenceBusinessQuery>(
                    ["getReferenceBusiness", { id: referenceId }],
                    oldData => {
                        return produce(oldData, draft => {
                            if (draft) {
                                draft.getReferenceBusiness =
                                    result.patchReferenceBusiness;
                            }
                        });
                    },
                );
            }
        },
        [patchReferenceBusinessMutation, client, referenceId],
    );
    const onAnswerCommitted = useCallback(
        (entityId: AnswerEntityId, answer: IAnswer) => {
            getReferenceBusinessQuery.refetch();
        },
        [getReferenceBusinessQuery],
    );

    const getLayoutContainer = useGetLayoutContainerByKeyQuery(
        { key: LayoutSectionParentKey.ReferenceBusiness },
        stableQueryOptions(),
    );
    const contactInformationSection =
        getLayoutContainer.data?.getLayoutContainerByKey.layoutSections.find(
            s => s.sectionKey === LayoutSectionKey.ReferenceBusinessContactInformation,
        );
    const reference = getReferenceBusinessQuery.data?.getReferenceBusiness;
    if (!reference || !contactInformationSection) {
        return <Spinner />;
    }
    const business = reference.businessContactEntity;
    return (
        <div className="space-y-1 lg:space-y-2">
            <AutosavingInput
                label="Organization Name"
                initial={business.name}
                onCommit={newVal =>
                    onChange({
                        id: reference.id,
                        businessContactEntity: { name: newVal },
                    })
                }
            />
            <SelectPro
                label="Organization Type"
                includePlaceholderOption
                value={business.contactType}
                onChange={e =>
                    onChange({
                        id: reference.id,
                        businessContactEntity: { contactType: e.target.value },
                    })
                }
                options={ReferralTypesOptions}
            />
            <PhoneInputE164
                label="Office Phone"
                initial={business.phone1}
                onCommit={newVal =>
                    onChange({
                        id: reference.id,
                        businessContactEntity: { phone1: newVal },
                    })
                }
            />
            <AutosavingInput
                label="Email"
                initial={reference.email}
                onCommit={newVal => onChange({ id: reference.id, email: newVal })}
            />
            <LayoutFormSection
                entityId={reference.id}
                entity={reference}
                entityType="ReferenceBusiness"
                layoutSection={contactInformationSection}
                mode="fieldsonly"
                answerMode={QuestionSource.Entity}
                onAnswerCommitted={onAnswerCommitted}
            />
        </div>
    );
};
